import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { createPortal } from 'react-dom';
import { TreeContainer, TreeControls, ControlButton, Legend, LegendItem, LegendDot } from './styles';
import {
  APPROVAL_STAGE_COLORS,
  COLORS,
  SEGMENT_TYPE,
  TREE_CHART_MODES
} from '../../../../constants/radar';
import {
  SEGMENT_PROCESS_APPLIES_TO,
  COMPETENCY_ASSIGNMENT_STATUS
} from '../../../../constants/segmentProcess';
import NodeModal from './components/NodeModal';
import SkillLevelSelector from './components/SkillLevelSelector';
import { FaPlus, FaMinus } from 'react-icons/fa';
import ConfirmationModal from '../../../common/ConfirmationModal';
import SafeDeleteDialog from '../../../common/SafeDeleteDialog';
import { useAlert } from '../../../../contexts/AlertContext';
import { getColor, getDisplayName, getDefaultLevelId } from '../../../../constants/skillLevels';

const TreeChart = ({
  data,
  selectedSubcategory,
  hoveredItem,
  onHover,
  onMouseOut,
  onSelection,
  zoomFactor,
  onAddNode,
  onEditNode,
  onRemoveNode,
  onMoveNode,
  tags,
  tagsLoading,
  tagsError,
  configMode = TREE_CHART_MODES.RADAR,
  selectedProject,
  projectData,
  expandedPath,
  setExpandedPath,
  onLeafToggle,
  isLeafAssigned,
  initialCheckedLeafs,
  isTreeExpanded,
  onTreeExpansionToggle,
  onSkillLevelChange,
  onSkillHistoryClick,
  onAddInternalNode,
  onRemoveInternalNode,
  selectedDashboard,
  skillLevels,
  showCompetencyLevels = true,
  showSkillHistory = true,
}) => {

  const svgRef = useRef(null);
  const [clickedItem, setClickedItem] = useState(null);
  const [currentTransform, setCurrentTransform] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { showAlert } = useAlert();
  const [checkedLeafs, setCheckedLeafs] = useState(new Set());
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [skillLevelPosition, setSkillLevelPosition] = useState(null);
  const [selectedSkillLevel, setSelectedSkillLevel] = useState(null);
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [nodeToMove, setNodeToMove] = useState(null);
  const [destinationNode, setDestinationNode] = useState(null);
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [localTreeData, setLocalTreeData] = useState(data);
  const [showLinkConfirmation, setShowLinkConfirmation] = useState(false);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
  const [manuallyCollapsedNodes, setManuallyCollapsedNodes] = useState(new Set());

  const isConfigurationMode = configMode === TREE_CHART_MODES.DASHBOARD;
  const isProjectMode = configMode === TREE_CHART_MODES.PROJECT;
  const isRadarMode = configMode === TREE_CHART_MODES.RADAR;
  const isTreeMode = configMode === TREE_CHART_MODES.TREE;
  const isEmployeeMode = configMode === TREE_CHART_MODES.EMPLOYEE;
  const isEmployeeReadOnlyMode = configMode === TREE_CHART_MODES.EMPLOYEE_READ_ONLY;
  const isProjectReadOnlyMode = configMode === TREE_CHART_MODES.PROJECT_READ_ONLY;
  const isSkillAssociationMode = configMode === TREE_CHART_MODES.SKILL_ASSOCIATION;

  // Helper to find first-level segment for a node and check if competency assignment is disabled
  const isCompetencyAssignmentDisabled = useCallback((nodeData, appliesTo) => {
    if (!data || !nodeData) return false;

    // Find the path from root to this node to get the first-level segment
    const findFirstLevelSegment = (node, target, depth = 0) => {
      if (!node) return null;
      if (node.id === target.id) {
        return depth === 1 ? node : null; // Return node only if it's at depth 1 (first level)
      }
      for (const child of (node.subcategories || [])) {
        const result = findFirstLevelSegment(child, target, depth + 1);
        if (result) return result;
        // If target is found in this subtree, return first-level child
        if (depth === 0) {
          const found = findNodeInTree(child, target);
          if (found) return child; // child is the first-level segment
        }
      }
      return null;
    };

    const findNodeInTree = (node, target) => {
      if (!node) return false;
      if (node.id === target.id) return true;
      for (const child of (node.subcategories || [])) {
        if (findNodeInTree(child, target)) return true;
      }
      return false;
    };

    const firstLevelSegment = findFirstLevelSegment(data, nodeData);
    if (!firstLevelSegment?.segmentProcesses) return false;

    const process = firstLevelSegment.segmentProcesses.find(
      p => p.appliesTo === appliesTo
    );
    return process?.competencyAssignment === COMPETENCY_ASSIGNMENT_STATUS.DISABLED;
  }, [data]);

  // Add this function before useEffect
  const isPartOfSubtree = (node, selectedNode) => {
    if (!selectedNode) return true;
    let current = node;
    while (current) {
      if (current.data.id === selectedNode.id) return true;
      current = current.parent;
    }
    return false;
  };

  // Add isPartOfPath function to check if node is in path from root to selected node
  const isPartOfPath = (node, selectedNode) => {
    if (!selectedNode || !node) return true;
    
    // Get the path from root to selectedNode
    const getPathToRoot = (n, path = []) => {
      if (!n) return path;
      path.unshift(n.data.id);
      if (!n.parent) return path;
      return getPathToRoot(n.parent, path);
    };
    
    // Check if node is in this path
    const pathToRoot = getPathToRoot(node);
    const selectedPathToRoot = getPathToRoot(
      // Find the d3 hierarchy node for the selectedNode
      d3.hierarchy(transformData(data))
        .descendants()
        .find(n => n.data.id === selectedNode.id)
    );
    
    // Node is part of path if it's in the selected node's path to root
    return selectedPathToRoot.includes(node.data.id);
  };

  // Initialize checkedLeafs when projectData or selectedProject changes
  useEffect(() => {

    // Reset when no project is selected
    if (!selectedProject) {
      setCheckedLeafs(new Set());
      return;
    }

    // Wait for projectData to be available
    if (!projectData) {
      return;
    }

    // Initialize from projectData
    if (projectData.leafSegmentProjects) {
      const checkedIds = new Set(
        projectData.leafSegmentProjects
          .filter(project => project.leafSegment != null)
          .map(project => project.leafSegment.id)
      );
      setCheckedLeafs(checkedIds);
    } else {
      setCheckedLeafs(new Set());
    }
  }, [selectedProject, projectData]);

  // Function to transform data structure
  const transformData = useCallback((data) => {
    if (!data) return { subcategories: [] };
    
    const transformed = { ...data };
    
    if (data.subcategories && data.subcategories.length > 0) {
      // Always keep the original subcategories reference
      transformed.subcategories = data.subcategories;
      
      // If node is expanded, transform its children
      if (expandedNodes.has(data.id)) {
        transformed.children = data.subcategories.map(child => transformData(child));
      } else {
        transformed.children = [];
      }
    } else {
      transformed.subcategories = [];
      transformed.children = [];
    }
    
    return transformed;
  }, [expandedNodes]);

  // Add effect to handle expandedPath changes but respect manually collapsed nodes
  useEffect(() => {
    if (expandedPath.length > 0) {
      // When expandedPath changes, update expandedNodes but respect manually collapsed nodes
      setExpandedNodes(prev => {
        const next = new Set(prev);
        for (const nodeId of expandedPath) {
          // Skip if this node was manually collapsed
          if (manuallyCollapsedNodes.has(nodeId)) {
            continue;
          }
          next.add(nodeId);
        }
        return next;
      });
    }
  }, [expandedPath, manuallyCollapsedNodes]);

  // Update the updateOpacities function to highlight both subtree and path to root
  const updateOpacities = useCallback((clickedItem) => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    
    if (!clickedItem) {
      // If no item is clicked, show everything at full opacity
      svg.selectAll('path.link').style('opacity', 1);
      svg.selectAll('.node').style('opacity', 1);
      svg.selectAll('.action-buttons').style('opacity', 0);
      return;
    }
    
    // Find the path from root to the clicked item
    const findPathToRoot = (currentNode, targetId, path = []) => {
      if (!currentNode) return null;
      
      // Add current node to the path
      const newPath = [...path, currentNode.data.id];
      
      // If this is the target, return the path
      if (currentNode.data.id === targetId) {
        return newPath;
      }
      
      // Check children if they exist
      if (currentNode.children) {
        for (const child of currentNode.children) {
          const foundPath = findPathToRoot(child, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      
      return null;
    };
    
    // Get the hierarchy root
    const hierarchyRoot = d3.hierarchy(transformData(data));
    
    // Find the path from root to the clicked item
    const pathToRoot = findPathToRoot(hierarchyRoot, clickedItem.id);
    
    // Create a Set for faster lookups
    const pathToRootSet = new Set(pathToRoot || []);
    
    // Function to check if a node should be highlighted
    const shouldHighlight = (node) => {
      // Highlight if node is in the path to root
      if (pathToRootSet.has(node.data.id)) return true;
      
      // Highlight if node is part of the subtree
      return isPartOfSubtree(node, clickedItem);
    };
    
    // Function to determine highlight intensity
    const getOpacityLevel = (node) => {
      // Full opacity for nodes in path to root
      if (pathToRootSet.has(node.data.id)) return 1;
      
      // Slightly reduced opacity for subtree nodes
      if (isPartOfSubtree(node, clickedItem)) return 0.9;
      
      // Much lower opacity for unrelated nodes
      return 0.2; // Make non-interactive nodes more clearly dimmed (was 0.25)
    };
    
    // Apply different opacity levels to links
    svg.selectAll('path.link')
      .style('opacity', d => {
        // Link is highlighted if both source and target are in the path or subtree
        const sourceHighlighted = pathToRootSet.has(d.source.data.id);
        const targetHighlighted = pathToRootSet.has(d.target.data.id);
        
        // Link is part of path to root
        if (sourceHighlighted && targetHighlighted) return 1;
        
        // Link connects to the subtree
        if (isPartOfSubtree(d.target, clickedItem)) return 0.8;
        
        // Other links
        return 0.15; // Make non-interactive links more clearly dimmed (was 0.2)
      })
      .style('stroke-width', d => {
        // Thicker stroke for path links
        const sourceHighlighted = pathToRootSet.has(d.source.data.id);
        const targetHighlighted = pathToRootSet.has(d.target.data.id);
        
        // Link is part of path to root
        if (sourceHighlighted && targetHighlighted) return '1px'; // Make path lines thicker (was 1px)
        
        // Default stroke width
        return '1px';
      })
      .style('stroke', d => {
        // Different color for path links
        const sourceHighlighted = pathToRootSet.has(d.source.data.id);
        const targetHighlighted = pathToRootSet.has(d.target.data.id);
        
        // Link is part of path to root
        if (sourceHighlighted && targetHighlighted) return COLORS.SECONDARY;
        
        // Default link color
        return '#DDE6EE';
      });

    // Update all node-related elements opacity
    svg.selectAll('.node')
      .style('opacity', d => getOpacityLevel(d))
      .style('filter', d => pathToRootSet.has(d.data.id) ? 'drop-shadow(0 0 2px rgba(0,0,0,0.3))' : 'none')
      .style('pointer-events', d => {
        // Disable pointer events for nodes that are not in the path or subtree
        if (!pathToRootSet.has(d.data.id) && !isPartOfSubtree(d, clickedItem)) {
          return 'none'; // Make non-interactive nodes unclickable
        }
        return 'auto';
      });

    // Ensure action buttons remain visible only for the clicked node
    svg.selectAll('.action-buttons')
      .style('opacity', d => d.data.id === clickedItem?.id ? 1 : 0);
  }, [data, transformData]);

  const handleModalSubmit = async (formData) => {
    if (modalMode === 'add') {
      onAddNode({ ...formData, parentNode: clickedItem });
    } else {
      const updatedNode = await onEditNode({ ...clickedItem, ...formData });
      if (updatedNode) {
        // Update clickedItem with the new data from the API response
        setClickedItem(prev => ({
          ...prev,
          ...updatedNode
        }));
      }
    }
  };

  // Add a ref to track if we've handled the initial state
  const hasHandledInitialState = useRef(false);

  const collectAllNodeIds = (data) => {
    const allNodeIds = new Set();
    const collectNodeIds = (node) => {
      if (!node) return;
      if (node.id) allNodeIds.add(node.id);
      if (node.subcategories) {
        node.subcategories.forEach(collectNodeIds);
      }
    };
    collectNodeIds(data);
    return allNodeIds;
  };

  // Effect to handle initial tree expansion state
  useEffect(() => {
    if (!hasHandledInitialState.current && isTreeExpanded !== undefined && data) {
      if (isTreeExpanded) {
        setExpandedNodes(collectAllNodeIds(data));
      }
      hasHandledInitialState.current = true;
    }
  }, [data, isTreeExpanded]);

  // Track previous isTreeExpanded value to detect actual toggle
  const prevIsTreeExpanded = useRef(isTreeExpanded);

  // Effect to handle tree expansion toggle - clear manually collapsed nodes on full expand/collapse
  // Only run when isTreeExpanded actually changes, not when data changes
  useEffect(() => {
    if (hasHandledInitialState.current && data && prevIsTreeExpanded.current !== isTreeExpanded) {
      prevIsTreeExpanded.current = isTreeExpanded;
      if (isTreeExpanded) {
        // Expand all nodes
        setExpandedNodes(collectAllNodeIds(data));
        // Clear manually collapsed nodes on full expand
        setManuallyCollapsedNodes(new Set());
      } else {
        // Collapse all nodes
        setExpandedNodes(new Set());
        // Clear manually collapsed nodes on full collapse
        setManuallyCollapsedNodes(new Set());
      }
    }
  }, [isTreeExpanded, data]);

  // Toggle function now uses the utility function
  const toggleTreeExpansion = useCallback(() => {
    const newExpandedState = !isTreeExpanded;
    if (onTreeExpansionToggle) {
      onTreeExpansionToggle(newExpandedState);
    }
  }, [isTreeExpanded, onTreeExpansionToggle]);

  // Update toggleNode to track manually collapsed nodes
  const toggleNode = useCallback((nodeId) => {
    // Close the skill level selector when toggling nodes
    setSkillLevelPosition(null);
    
    // Check if we're closing a node and if clickedItem would be hidden
    const isClosingNode = expandedNodes.has(nodeId);
    let shouldUpdateSelection = false;
    let nodeBeingClosed = null;
    
    if (isClosingNode && clickedItem) {
      // Find the node being collapsed in our data
      const findNode = (root, id) => {
        if (!root) return null;
        if (root.id === id) return root;
        if (!root.subcategories) return null;
        
        for (const child of root.subcategories) {
          const found = findNode(child, id);
          if (found) return found;
        }
        return null;
      };
      
      // Check if the clicked item is a descendant of the node being collapsed
      const isDescendant = (parent, childId) => {
        if (!parent || !parent.subcategories) return false;
        
        // Direct child check
        if (parent.subcategories.some(child => child.id === childId)) return true;
        
        // Recursive check for deeper descendants
        return parent.subcategories.some(child => isDescendant(child, childId));
      };
      
      // Get the node being collapsed
      nodeBeingClosed = findNode(data, nodeId);
      
      // Check if clicked item is a descendant of the node being collapsed
      if (nodeBeingClosed) {
        shouldUpdateSelection = isDescendant(nodeBeingClosed, clickedItem.id);
      }
    }

    // Update manually collapsed nodes set
    if (isClosingNode) {
      setManuallyCollapsedNodes(prev => {
        const next = new Set(prev);
        next.add(nodeId);
        return next;
      });
    } else {
      setManuallyCollapsedNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }

    // Toggle the expansion state
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });

    // Force a re-render of the tree
    setLocalTreeData(prevData => ({ ...prevData }));
    
    // If we're closing a node that contains the current selection,
    // select the closed node instead
    if (shouldUpdateSelection && nodeBeingClosed) {
      setClickedItem(nodeBeingClosed);
      if (onSelection) {
        onSelection(nodeBeingClosed);
      }
    }
    
    // Maintain opacity effect after node toggle
    requestAnimationFrame(() => {
      updateOpacities(shouldUpdateSelection ? nodeBeingClosed : clickedItem);
    });
  }, [clickedItem, updateOpacities, expandedNodes, data, onSelection]);

  // Modify the useEffect that manages path expansion to respect manually collapsed nodes
  useEffect(() => {
    if (clickedItem) {
      // Find path from root to clicked item
      const findPathToNode = (node, targetId, path = []) => {
        if (!node) return null;
        
        // Add current node to the path
        const newPath = [...path, node.id];
        
        // If this is the target, return the path
        if (node.id === targetId) {
          return newPath;
        }
        
        // Check children if they exist
        if (node.subcategories) {
          for (const child of node.subcategories) {
            const foundPath = findPathToNode(child, targetId, newPath);
            if (foundPath) return foundPath;
          }
        }
        
        return null;
      };
      
      // Find the path from root to the selected node
      const nodePath = findPathToNode(data, clickedItem.id);
      
      if (nodePath) {
        // Ensure all nodes in the path are expanded, except manually collapsed ones
        setExpandedNodes(prev => {
          const next = new Set(prev);
          
          for (const nodeId of nodePath) {
            // Skip if this node was manually collapsed
            if (manuallyCollapsedNodes.has(nodeId)) {
              continue;
            }
            next.add(nodeId);
          }
          
          return next;
        });
        
        // Force a tree re-render to ensure expanded nodes are shown
        setLocalTreeData(prevData => ({ ...prevData }));
        
        // Update the expandedPath (for parent components to track)
        if (setExpandedPath && JSON.stringify(nodePath) !== JSON.stringify(expandedPath)) {
          setExpandedPath(nodePath);
        }
      }
    }
  }, [clickedItem, data, expandedPath, setExpandedPath, manuallyCollapsedNodes]);

  // Update the handleCheckboxChange function to use viewport coordinates
  const handleCheckboxChange = async (event, nodeData, isChecked) => {
    const currentCheckbox = event.target;
    currentCheckbox.disabled = true;

    try {
      if (isEmployeeMode) {
        // Check if competency assignment is disabled for this branch
        const competencyDisabled = isCompetencyAssignmentDisabled(nodeData, SEGMENT_PROCESS_APPLIES_TO.EMPLOYEE);

        if (!competencyDisabled) {
          // Show skill level selector only if competency assignment is enabled
          const checkboxElement = event.target;
          const checkboxRect = checkboxElement.getBoundingClientRect();

          // Find and set the clicked item based on the node ID
          const root = d3.hierarchy(transformData(data));
          const selectedNode = root.descendants().find(d => d.data.id === nodeData.id);
          if (selectedNode) {
            setClickedItem(selectedNode.data);
            setSelectedSkillLevel(isLeafAssigned(nodeData.id) ? selectedNode.data.competencyLevelId || getDefaultLevelId(skillLevels) : getDefaultLevelId(skillLevels));

            // Position using viewport coordinates
            setSkillLevelPosition({
              x: checkboxRect.right + 10,
              y: checkboxRect.top + (checkboxRect.height / 2)
            });
          }
          // Don't change the checkbox state yet
          currentCheckbox.checked = !isChecked;
          return;
        }

        // Competency assignment is disabled - save selection without showing level selector
        // Use fire-and-forget pattern (same as handleSkillLevelSelect) to avoid blocking the render cycle
        setCheckedLeafs(prev => {
          const next = new Set(prev);
          if (isChecked) {
            next.add(nodeData.id);
          } else {
            next.delete(nodeData.id);
          }
          return next;
        });

        // Save the selection with skipCompetencyLevel flag - don't await, let it run in background
        if (onSkillLevelChange) {
          const levelToSave = isChecked ? null : getDefaultLevelId(skillLevels);
          onSkillLevelChange(nodeData.leafSegmentId, levelToSave, { skipCompetencyLevel: true, segmentId: nodeData.id, isCustomSegment: nodeData.isCustomSegment });
        }
        return;
      }

      setCheckedLeafs(prev => {
        const next = new Set(prev);
        if (isChecked) {
          next.add(nodeData.id);
        } else {
          next.delete(nodeData.id);
        }
        return next;
      });

      if (onLeafToggle) {
        await onLeafToggle(nodeData.leafSegmentId, isChecked, { isCustomSegment: nodeData.isCustomSegment });
      }
      
    } catch (error) {
      // Revert checkbox state on error
      currentCheckbox.checked = !isChecked;
      
      if (!isChecked) {
        setCheckedLeafs(prev => new Set([...prev, nodeData.id]));
      } else {
        setCheckedLeafs(prev => {
          const next = new Set(prev);
          next.delete(nodeData.id);
          return next;
        });
      }
    } finally {
      currentCheckbox.disabled = false;
    }
  };

  useEffect(() => {
     setClickedItem(selectedSubcategory);
  }, [selectedSubcategory])

  useEffect(() => {
    if (!data || !svgRef.current) return;

    // Ensure we have at least an empty structure to work with
    const dataToRender = localTreeData || { subcategories: [] };

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Create hierarchy with null checks
    const hierarchy = d3.hierarchy(transformData(dataToRender));

    // Set dimensions based on container and content
    const width = 900;
    const margin = { top: 35, right: 100, bottom: 50, left: 100 };

    const dx = 10;
    const dy = width / (hierarchy.height + 1);

    // Generate the cluster layout
    const cluster = d3.tree()
      .nodeSize([dx, dy])
      .separation((a, b) => {
         return a.parent === b.parent ? 2 : 4;
       });

    const root = cluster(hierarchy);

    // Compute the extent of the tree
    let x0 = Infinity;
    let x1 = -x0;
    root.each(d => {
        if (d.x > x1) x1 = d.x;
        if (d.x < x0) x0 = d.x;
    });

    // Compute the adjusted height of the tree
    const height = x1 - x0 + margin.top + margin.bottom;

    // Update SVG dimensions
    const svg = d3.select(svgRef.current)
      .attr("viewBox", [-dy / 3 - margin.top, x0 - dx - margin.top, (1+1/zoomFactor)*width, height + margin.bottom])
      .attr('preserveAspectRatio', 'xMinYMin slice');

    // Apply transformation based on zoom props with initial center position
    const g = svg.append("g")
        .attr("transform", `translate(0,0)`);

    // Add links between nodes
    g.selectAll('path.link')
      .data(root.links())
      .join('path')
      .attr('class', 'link')
      .attr('d', d3.linkHorizontal()
        .x(d => d.y)
        .y(d => d.x))
      .style('fill', 'none')
      .style('stroke', '#DDE6EE')
      .style('stroke-width', '1px')
      //.style('opacity', d => clickedItem ? (isPartOfSubtree(d.target, clickedItem) ? 1 : 0.3) : 1);

    // Add nodes
    const node = g.selectAll('g.node')
      .data(root.descendants())
      .join('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.y},${d.x})`);

    // Add checkboxes for leaf nodes when in project/dashboard/employee mode
    if ((isProjectMode && selectedProject && !isProjectReadOnlyMode) || isConfigurationMode || (isEmployeeMode && !isEmployeeReadOnlyMode) || isSkillAssociationMode) {
      node.filter(d => d.data.segmentType === SEGMENT_TYPE.LEAF)
        .append('foreignObject')
        .attr('width', 20)
        .attr('height', 20)
        .attr('x', -25)
        .attr('y', -10)
        .append('xhtml:input')
        .attr('type', 'checkbox')
        .attr('data-node-id', d => d.data.id)
        .property('checked', d => {
          return checkedLeafs.has(d.data.id) || (isLeafAssigned && isLeafAssigned(d.data.id));
        })
        .style('accent-color', COLORS.SECONDARY)
        .on('change', function(event, d) {
          handleCheckboxChange(event, d.data, this.checked);
        });
    }

    // Add skill level display for leaf nodes in employee mode
    if (isEmployeeMode || isEmployeeReadOnlyMode) {
      // Only show competency levels if showCompetencyLevels is true
      if (showCompetencyLevels) {
        node.filter(d => d.data.segmentType === SEGMENT_TYPE.LEAF && d.data.competencyLevelId && d.data.competencyLevelId !== getDefaultLevelId(skillLevels))
          .append('foreignObject')
          .attr('width', 100)  // Wider to accommodate the skill level text
          .attr('height', 20)
          .attr('x', d => {
            const textLength = d.data.name.length * 8; // Approximate text width
            return textLength + 10; // Position after the node text
          })
          .attr('y', -13)
          .append('xhtml:div')
          .style('background-color', d => getColor(d.data.competencyLevelId, skillLevels))
          .style('color', '#333')
          .style('padding', '2px 8px')
          .style('border-radius', '4px')
          .style('font-size', '10px')
          .style('display', 'inline-block')
          .style('white-space', 'nowrap')
          .text(d => getDisplayName(d.data.competencyLevelId, skillLevels));
      }

      // Add history icon for nodes with skillHistory that has more than one entry (only if showSkillHistory is true)
      if (showSkillHistory) {
        node.filter(d => d.data.segmentType === SEGMENT_TYPE.LEAF && Array.isArray(d.data.skillHistory) && d.data.skillHistory.length > 1)
          .append('foreignObject')
          .attr('width', 24)
          .attr('height', 24)
          .attr('x', d => {
            const textLength = d.data.name.length * 8;
            const hasSkillLevel = showCompetencyLevels && d.data.competencyLevelId && d.data.competencyLevelId !== getDefaultLevelId(skillLevels);
            return textLength + (hasSkillLevel ? 120 : 10); // Position after skill level or node text
          })
          .attr('y', -9)
          .append('xhtml:div')
          .style('cursor', 'pointer')
          .style('color', COLORS.SECONDARY)
          .style('display', 'flex')
          .style('align-items', 'center')
          .style('justify-content', 'center')
          .html('<svg style="width: 14px; height: 14px;" viewBox="0 0 512 512"><path fill="currentColor" d="M64 64c0-17.7-14.3-32-32-32S0 46.3 0 64V400c0 44.2 35.8 80 80 80H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H80c-8.8 0-16-7.2-16-16V64zm406.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L320 210.7l-57.4-57.4c-12.5-12.5-32.8-12.5-45.3 0l-112 112c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L240 221.3l57.4 57.4c12.5 12.5 32.8 12.5 45.3 0l128-128z"/></svg>')
          .on('click', (event, d) => {
            event.stopPropagation();
            if (onSkillHistoryClick) {
              onSkillHistoryClick(d.data);
            }
          });
      }
    }

    // Add this BEFORE your existing node circle code
    // This creates the highlight effect for valid drop targets
    node.append('circle')
      .attr('class', 'node-highlight')
      .attr('r', 12)
      .style('fill', 'none')
      .style('stroke', d => {
        if (isMoveMode && nodeToMove && isValidDropTarget(d, nodeToMove)) {
          return '#FFA500';
        }
        return 'none';
      })
      .style('stroke-width', 2)
      .style('stroke-dasharray', '3,3')
      .style('opacity', d => {
        if (isMoveMode && nodeToMove && isValidDropTarget(d, nodeToMove)) {
          return 0.5;
        }
        return 0;
      });

    // Modify your existing node circle code:
    node.append('circle')
      .attr('r', d => d.data.segmentType === SEGMENT_TYPE.LEAF ? 3 : 5)
      .style('fill', d => {
        if (isMoveMode && nodeToMove) {
          if (d.data.id === nodeToMove.data.id) {
            return '#6666FF'; // Source node being moved
          }
          if (isValidDropTarget(d, nodeToMove)) {
            return '#FFA50020'; // Valid drop target
          }
        }

        // Handle approval stage colors
        if (d.data.approvalStage) {
          const stage = d.data.approvalStage.toUpperCase();
          return APPROVAL_STAGE_COLORS[stage];
        }

        // Handle leaf nodes
        if (d.data.segmentType === SEGMENT_TYPE.LEAF) {
          return COLORS.PRIMARY;
        }

        // Handle expandable/expanded nodes
        if (d.data.subcategories && d.data.subcategories.length > 0) {
          return expandedNodes.has(d.data.id) ? COLORS.EXPANDED : COLORS.PRIMARY;
        }

        // Empty non-leaf nodes
        return COLORS.EXPANDED;
      })
      .style('stroke', d => {
        if (isMoveMode && nodeToMove) {
          if (d.data.id === nodeToMove.data.id) {
            return '#6666FF';
          }
          if (isValidDropTarget(d, nodeToMove)) {
            return '#FFA500';
          }
        }
        return 'white';
      })
      .style('stroke-width', d => {
        if (isMoveMode && nodeToMove && isValidDropTarget(d, nodeToMove)) {
          return 2;
        }
        return 1;
      })
      .style('cursor', d => {
        // In move mode
        if (isMoveMode && nodeToMove && isValidDropTarget(d, nodeToMove)) {
          return 'pointer';
        }
        
        // When a node is selected, only show pointer for highlighted nodes
        if (clickedItem) {
          // Check if node is in highlighted path or subtree
          const inHighlightedPath = isPartOfPath(d, clickedItem);
          const inHighlightedSubtree = isPartOfSubtree(d, clickedItem);
          
          if (!inHighlightedPath && !inHighlightedSubtree) {
            return 'default'; // Not interactive when outside highlighted areas
          }
        }
        
        // Default cursor for expandable nodes
        if (d.data.subcategories && d.data.subcategories.length > 0) {
          return 'pointer';
        }
        
        return 'default';
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        
        // First handle move mode
        if (isMoveMode && nodeToMove && isValidDropTarget(d, nodeToMove)) {
          setDestinationNode(d);
          setShowMoveConfirmation(true);
          return;
        }
        
        // Check if we should allow interaction with this node
        if (clickedItem) {
          // Get whether node is in highlighted path or subtree
          const inHighlightedPath = isPartOfPath(d, clickedItem);
          const inHighlightedSubtree = isPartOfSubtree(d, clickedItem);
          
          // Skip interaction if node is outside highlighted areas
          if (!inHighlightedPath && !inHighlightedSubtree) {
            return;
          }
        }
        
        // Then handle employee mode
        if (isEmployeeMode) {
          handleEmployeeNodeClick(event, d);
          return;
        }
        
        // Handle node expansion for non-leaf nodes
        if (d.data.subcategories && d.data.subcategories.length > 0) {
          toggleNode(d.data.id);
          return; // Important: return here to prevent selection
        }
      });

    // Update the text click handler to ONLY handle selection
    node.append('text')
      .attr('dy', '.24em')
      .attr('x', d => d.children ? -6 : 6) // Adjust text position to avoid overlap with checkbox
      .attr('text-anchor', d => d.children ? 'end' : 'start')
      .text(d => d.data.name)
      .attr("stroke-linejoin", "round")
      .attr("stroke-width", 3)
      .attr("stroke", COLORS.BACKGROUND_PRIMARY)
      .attr("paint-order", "stroke")
      .style('font-size', '8px')
      .style('fill', d => {
        if (d.data.approvalStage) {
          return COLORS.SECONDARY;
        }
        return COLORS.PRIMARY;
      })
      .style('font-weight', d => 
        hoveredItem?.id === d.data.id || clickedItem?.id === d.data.id ? 'bold' : 'normal'
      )
      .style('cursor', d => {
        // When a node is selected, only show pointer for highlighted nodes
        if (clickedItem) {
          // Check if node is in highlighted path or subtree
          const inHighlightedPath = isPartOfPath(d, clickedItem);
          const inHighlightedSubtree = isPartOfSubtree(d, clickedItem);
          
          if (!inHighlightedPath && !inHighlightedSubtree) {
            return 'default'; // Not interactive when outside highlighted areas
          }
        }
        
        return 'pointer';
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        
        // Check if we should allow interaction with this node
        if (clickedItem) {
          // Get whether node is in highlighted path or subtree
          const inHighlightedPath = isPartOfPath(d, clickedItem);
          const inHighlightedSubtree = isPartOfSubtree(d, clickedItem);
          
          // Skip interaction if node is outside highlighted areas
          if (!inHighlightedPath && !inHighlightedSubtree) {
            return;
          }
        }
        
        // Only handle selection, no expansion logic here
        const newClickedItem = clickedItem?.id === d.data.id ? null : d.data;
        setClickedItem(newClickedItem);
        
        // Hide all action buttons first
        g.selectAll('.action-buttons')
          .style('opacity', 0)
          .style('display', 'none');
        
        // Show action buttons only for clicked item
        if (newClickedItem) {
          d3.select(event.currentTarget.parentNode)
            .select('.action-buttons')
            .style('opacity', 1)
            .style('display', 'block');
        }
        
        if (onSelection) {
          onSelection(newClickedItem);
        }
      });

    // Add action buttons container - moved to be last
    const actionButtons = node.append('g')
      .attr('class', 'action-buttons')
      .style('opacity', d => d.data.id === clickedItem?.id ? 1 : 0)
      .style('display', d => d.data.id === clickedItem?.id ? 'block' : 'none')
      .attr('transform', 'translate(20, -20)');

    // Add standard action buttons (add/edit/delete/move)
    if (isTreeMode || isRadarMode) {
      // Add button
      actionButtons.append('circle')
        .attr('class', 'add-button')
        .style('opacity', d => {
          // Only show add button if:
          // 1. This is the clicked item
          // 2. This is not a leaf node
          // 3. We are in radar or tree configuration mode
          return (d.data.id === clickedItem?.id && 
                  d.data.segmentType !== SEGMENT_TYPE.LEAF && 
                  (isTreeMode || isRadarMode)) ? 1 : 0;
        })
        .style('display', d => {
          return (d.data.id === clickedItem?.id && 
                  d.data.segmentType !== SEGMENT_TYPE.LEAF && 
                  (isTreeMode || isRadarMode)) ? 'block' : 'none';
        })
        .attr('r', 6)
        .attr('cx', 0)
        .attr('cy', 0)
        .style('fill', COLORS.PRIMARY)
        .style('cursor', d => d.data.segmentType !== SEGMENT_TYPE.LEAF ? 'pointer' : 'not-allowed')
        .on('click', (event, d) => {
          // Only allow adding if not a leaf node
          if (d.data.segmentType !== SEGMENT_TYPE.LEAF) {
            event.stopPropagation();
            setModalMode('add');
            setModalOpen(true);
          }
        });

      // Edit button
      actionButtons.append('circle')
        .attr('class', 'edit-button')
        .style('opacity', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 1 : 0;
        })
        .style('display', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 'block' : 'none';
        })
        .attr('r', 6)
        .attr('cx', 15)
        .attr('cy', 0)
        .style('fill', COLORS.SECONDARY)
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          setModalMode('edit');
          setModalOpen(true);
        });

      // Remove button
      actionButtons.append('circle')
        .attr('class', 'remove-button')
        .style('opacity', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 1 : 0;
        })
        .style('display', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 'block' : 'none';
        })
        .attr('r', 6)
        .attr('cx', 30)
        .attr('cy', 0)
        .style('fill', d => d.children ? '#cccccc' : '#ff4444') // Gray if has children, red if not
        .style('cursor', d => d.children ? 'not-allowed' : 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          // Only allow removal if node has no children
          if (!d.children) {
            setShowDeleteConfirmation(true);
          }
        });

      // Move button
      actionButtons.append('circle')
        .attr('class', 'move-button')
        .style('opacity', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 1 : 0;
        })
        .style('display', d => {
          return (d.data.id === clickedItem?.id && 
                  d.depth > 0 && 
                  (isTreeMode || isRadarMode)) ? 'block' : 'none';
        })
        .attr('r', 6)
        .attr('cx', 60)
        .attr('cy', 0)
        .style('fill', '#6666FF')
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          setIsMoveMode(true);
          setNodeToMove(d);
          setClickedItem(null); 
          updateOpacities(null);
        });

      // Add button texts
      actionButtons.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .style('fill', 'white')
        .style('font-size', '10px')
        .style('opacity', d => d.data.segmentType !== SEGMENT_TYPE.LEAF ? 1 : 0)
        .style('display', d => d.data.segmentType !== SEGMENT_TYPE.LEAF ? 'block' : 'none')
        .text('+');

      actionButtons.append('text')
        .attr('x', 15)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .style('fill', 'white')
        .style('font-size', '10px')
        .text('✎');

      actionButtons.append('text')
        .attr('x', 30)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .style('fill', 'white')
        .style('font-size', '10px')
        .style('opacity', d => d.children ? 0.5 : 1) // Dim the text for disabled state
        .text('×');

      actionButtons.append('text')
        .attr('x', 60)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .style('fill', 'white')
        .style('font-size', '8px')
        .text('M');
    }

    // Add link/unlink buttons for internal nodes in dashboard mode - last to be on top
    if (isConfigurationMode) {
      actionButtons.append('circle')
        .attr('class', 'link-button')
        .style('opacity', d => {
          return (d.data.id === clickedItem?.id && 
                  d.data.segmentType === SEGMENT_TYPE.INTERNAL && 
                  d.depth > 0) ? 1 : 0;
        })
        .style('display', d => {
          return (d.data.id === clickedItem?.id && 
                  d.data.segmentType === SEGMENT_TYPE.INTERNAL && 
                  d.depth > 0) ? 'block' : 'none';
        })
        .attr('r', 6)
        .attr('cx', -20)
        .attr('cy', 0)
        .style('fill', d => {
          // Check if the node's ID exists in dashboardInternalSegments
          const isUnlinked = selectedDashboard?.dashboardInternalSegments?.some(
            internalSegment => internalSegment.internalSegment.segment.id === d.data.id
          );
          return isUnlinked ? '#44aa44' : '#ff4444';  // Green for link action, red for unlink action
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          // Check if the node's ID exists in dashboardInternalSegments
          const isUnlinked = selectedDashboard?.dashboardInternalSegments?.some(
            internalSegment => internalSegment.internalSegment.segment.id === d.data.id
          );
          if (isUnlinked) {
            setShowLinkConfirmation(true);  // Show link confirmation when node is unlinked
          } else {
            setShowUnlinkConfirmation(true);  // Show unlink confirmation when node is linked
          }
        });

      actionButtons.append('text')
        .attr('x', -20)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.3em')
        .style('fill', 'white')
        .style('font-size', '8px')
        .text(d => {
          // Check if the node's ID exists in dashboardInternalSegments
          const isUnlinked = selectedDashboard?.dashboardInternalSegments?.some(
            internalSegment => internalSegment.internalSegment.segment.id === d.data.id
          );
          return isUnlinked ? '⊕' : '⊗';  // Plus for link action, X for unlink action
        });
    }

    // Update existing nodes
    node.select('text')
      .attr('dy', '0.31em')
      .attr('x', d => d.children ? -8 : 8)
      .attr('text-anchor', d => d.children ? 'end' : 'start')
      .style('fill', d => {
        if (isConfigurationMode && d.data.segmentType === SEGMENT_TYPE.INTERNAL && d.depth > 0) {
          // Check if the node is unlinked (exists in dashboardInternalSegments)
          const isUnlinked = selectedDashboard?.dashboardInternalSegments?.some(
            internalSegment => internalSegment.internalSegment.segment.id === d.data.id
          );
          return isUnlinked ? '#ff4444' : COLORS.PRIMARY;  // Red for unlinked nodes
        }
        return COLORS.PRIMARY;
      })
      .style('opacity', d => {
        if (isConfigurationMode && d.data.segmentType === SEGMENT_TYPE.INTERNAL && d.depth > 0) {
          // Check if the node is unlinked
          const isUnlinked = selectedDashboard?.dashboardInternalSegments?.some(
            internalSegment => internalSegment.internalSegment.segment.id === d.data.id
          );
          return isUnlinked ? 0.6 : 1;  // Semi-transparent for unlinked nodes
        }
        return 1;
      })
      .style('text-decoration', 'none')  // Remove line-through completely
      .text(d => d.data.name);

    // After rendering is complete, update opacities
    requestAnimationFrame(() => {
      updateOpacities(clickedItem);
    });
  }, [
    data,
    clickedItem,
    expandedNodes,
    configMode,
    selectedProject,
    checkedLeafs,
    isLeafAssigned,
    zoomFactor,
    isMoveMode,
    nodeToMove,
    localTreeData,
    transformData,
    isConfigurationMode,
    showLinkConfirmation,
    showUnlinkConfirmation,
    selectedDashboard,
    updateOpacities,
    showCompetencyLevels,
    showSkillHistory
  ]);

  const handleModalClose = () => {
    setModalOpen(false);
    setModalMode('add'); // Reset mode when closing
  };

  const handleAddClick = (event, d) => {
    event.stopPropagation();
    setModalMode('add');
    setModalOpen(true);
  };

  // Modify the render function to show/hide controls based on mode
  const renderControls = () => {
    if (isConfigurationMode || isRadarMode || isProjectMode || isTreeMode || isEmployeeMode || isEmployeeReadOnlyMode || isProjectReadOnlyMode || isSkillAssociationMode) {
      return (
        <TreeControls>
          <ControlButton onClick={toggleTreeExpansion}>
            {isTreeExpanded ? <FaMinus size={12} /> : <FaPlus size={12} />}
            <span>{isTreeExpanded ? 'Collapse' : 'Expand'}</span>
          </ControlButton>

          {/* Add new button to exit move mode if isMoveMode */}
          {isMoveMode && (
            <ControlButton onClick={handleCancelMove}>
              Cancel Move
            </ControlButton>
          )}

          <Legend>
            <LegendItem>
              <LegendDot color={COLORS.PRIMARY} />
              <span>Node can be expanded</span>
            </LegendItem>
            <LegendItem>
              <LegendDot color={COLORS.EXPANDED} />
              <span>Node already expanded or empty</span>
            </LegendItem>
          </Legend>
        </TreeControls>
      );
    }
    return null;
  };

  useEffect(() => {
    setCheckedLeafs(initialCheckedLeafs);
  }, [initialCheckedLeafs]);

  // Update the handleEmployeeNodeClick to handle skill history
  const handleEmployeeNodeClick = (event, d) => {
    event.stopPropagation();

    if (d.data.segmentType === SEGMENT_TYPE.LEAF) {

      const nodeElement = event.target.parentNode;
      const nodeRect = nodeElement.getBoundingClientRect();

      const defaultLevelId = getDefaultLevelId(skillLevels);
      setSkillLevelPosition({
        x: nodeRect.right + 10,
        y: nodeRect.top + (nodeRect.height / 2)
      });
      setClickedItem(d.data);
      // Use the node's current competency level if assigned, otherwise default
      setSelectedSkillLevel(d.data.competencyLevelId || defaultLevelId);
    } else if (d.data.subcategories && d.data.subcategories.length > 0) {
      toggleNode(d.data.id);
    }
  };

  // Remove the right-click event listener since we're using an icon now
  useEffect(() => {
    const svg = svgRef.current;
    if (svg && (isEmployeeMode || isEmployeeReadOnlyMode)) {
      svg.removeEventListener('contextmenu', (e) => e.preventDefault());
    }
  }, [isEmployeeMode, isEmployeeReadOnlyMode]);

  // Add this function to handle skill level selection
  const handleSkillLevelSelect = (level) => {

    setSelectedSkillLevel(level);
    if (onSkillLevelChange) {
      onSkillLevelChange(clickedItem.leafSegmentId, level, { segmentId: clickedItem.id, isCustomSegment: clickedItem.isCustomSegment });
    }
    // Close the selector after selection
    setSkillLevelPosition(null);
    setClickedItem(null);
  };

  // Add useEffect for handling clicks outside the SkillLevelSelector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skillLevelPosition && clickedItem) {
        // Check if the click is outside both the SkillLevelSelector and the checkbox
        const skillLevelElement = document.querySelector('.skill-level-selector');
        const checkboxElement = document.querySelector(`input[type="checkbox"][data-node-id="${clickedItem.id}"]`);
        
        if (skillLevelElement && 
            !skillLevelElement.contains(event.target) && 
            (!checkboxElement || !checkboxElement.contains(event.target))) {
          setSkillLevelPosition(null);
          setClickedItem(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [skillLevelPosition, clickedItem]);

  // A small helper to see if "candidate" is a descendant of "root" for restricting possible destinations
  const isDescendant = (root, candidate) => {
    if (!root) return false;
    let current = candidate;
    while (current) {
      if (current.data.id === root.data.id) return true;
      current = current.parent;
    }
    return false;
  };

  // -------------- CANCEL MOVE BUTTON IN CONTROLS --------------
  const handleCancelMove = () => {
    setIsMoveMode(false);
    setNodeToMove(null);
    setDestinationNode(null);
    setShowMoveConfirmation(false);
  };

  // 2) Helpers to remove node and attach it elsewhere in localTreeData
  const findNodeById = (root, id) => {
    if (!root || !id) return null;
    if (root.id === id) return root;
    if (!root.subcategories || !Array.isArray(root.subcategories)) return null;
    
    for (const child of root.subcategories) {
      if (!child) continue; // Skip null/undefined children
      const found = findNodeById(child, id);
      if (found) return found;
    }
    return null;
  };

  const removeNodeFromParent = (root, nodeId) => {
    if (!root || !nodeId) return false;
    if (!root.subcategories || !Array.isArray(root.subcategories)) return false;
    
    // remove if direct child
    const idx = root.subcategories.findIndex(child => child && child.id === nodeId);
    if (idx !== -1) {
      root.subcategories.splice(idx, 1);
      return true;
    }
    
    // otherwise, recurse
    for (const child of root.subcategories) {
      if (!child) continue; // Skip null/undefined children
      if (removeNodeFromParent(child, nodeId)) {
        return true;
      }
    }
    return false;
  };

  // Moves a node (by ID) from wherever it is to become a child of the destination node
  const moveNodeInTree = (treeRoot, sourceId, destId) => {
    if (!treeRoot || !sourceId || !destId) {
      console.warn('Invalid parameters for moveNodeInTree');
      return;
    }

    try {
      // Find the source node first
      const sourceNode = findNodeById(treeRoot, sourceId);
      if (!sourceNode) {
        console.warn('Source node not found');
        return;
      }

      // Remove from parent's subcategories
      const removed = removeNodeFromParent(treeRoot, sourceId);
      if (!removed) {
        console.warn('Failed to remove source node from its parent');
        return;
      }

      // Find the destination node
      const destNode = findNodeById(treeRoot, destId);
      if (!destNode) {
        console.warn('Destination node not found');
        return;
      }

      // Ensure subcategories exists and is an array
      if (!destNode.subcategories) {
        destNode.subcategories = [];
      }

      // Re‐parent → push sourceNode into the destination's subcategories
      destNode.subcategories.push(sourceNode);
    } catch (error) {
      console.error('Error in moveNodeInTree:', error);
    }
  };

  useEffect(() => {
    setLocalTreeData(data || { subcategories: [] }); // Provide a default empty structure
  }, [data]);

  // In your isDescendant helper function, add a check for leaf nodes:
  const isValidDropTarget = (node, sourceNode) => {
    if (!node || !sourceNode) return false;
    
    // Don't allow dropping on leaf nodes
    if (node.data.segmentType === SEGMENT_TYPE.LEAF) return false;
    
    // Don't allow dropping on self or descendants
    if (isDescendant(sourceNode, node)) return false;
    
    // Don't allow dropping if source and target are the same
    if (node.data.id === sourceNode.data.id) return false;

    // Don't allow dropping on current parent
    if (sourceNode.parent && sourceNode.parent.data.id === node.data.id) return false;

    return true;
  };

  useEffect(() => {
    if (!svgRef.current) return;
    updateOpacities(clickedItem);
  }, [clickedItem, zoomFactor]);

  return (
    <TreeContainer>
      {renderControls()}
      <svg ref={svgRef} />
      <NodeModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        node={clickedItem}
        mode={modalMode}
        tags={tags}
        tagsLoading={tagsLoading}
        tagsError={tagsError}
        style={{ zIndex: 9999 }}
      />
      {/* Safe delete dialog for leaf segments — shows usage analysis and replace/force-delete options */}
      {clickedItem?.segmentType === SEGMENT_TYPE.LEAF && (
        <SafeDeleteDialog
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setClickedItem(null);
          }}
          onDeleted={(segmentId) => {
            onRemoveNode({ ...clickedItem, _alreadyDeleted: true });
            setClickedItem(null);
          }}
          segmentId={clickedItem?.id}
          segmentName={clickedItem?.name}
        />
      )}
      {/* Simple confirmation for internal (non-leaf) segments */}
      {clickedItem?.segmentType !== SEGMENT_TYPE.LEAF && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setClickedItem(null);
          }}
          onConfirm={() => {
            onRemoveNode(clickedItem);
            setShowDeleteConfirmation(false);
            setClickedItem(null);
          }}
          title="Delete Node"
          message={`Are you sure you want to delete "${clickedItem?.name}"? This action cannot be undone.`}
          actionButtonText="Delete"
          style={{
            zIndex: 99999,
            position: 'relative'
          }}
          overlayStyle={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99998,
            display: showDeleteConfirmation ? 'block' : 'none'
          }}
        />
      )}
      <ConfirmationModal
        isOpen={showLinkConfirmation}
        onClose={() => {
          setShowLinkConfirmation(false);
          setClickedItem(null);
        }}
        onConfirm={() => {
          onRemoveInternalNode(clickedItem);
          setShowLinkConfirmation(false);
          setClickedItem(null);
        }}
        title="Link Internal Node"
        message={`Are you sure you want to link "${clickedItem?.name}" back to this dashboard?`}
        actionButtonText="Link"
        style={{ 
          zIndex: 99999,
          position: 'relative'
        }}
        overlayStyle={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99998,
          display: showLinkConfirmation ? 'block' : 'none'
        }}
      />
      <ConfirmationModal
        isOpen={showUnlinkConfirmation}
        onClose={() => {
          setShowUnlinkConfirmation(false);
          setClickedItem(null);
        }}
        onConfirm={() => {
          onAddInternalNode(clickedItem);
          setShowUnlinkConfirmation(false);
          setClickedItem(null);
        }}
        title="Unlink Internal Node"
        message={`Are you sure you want to unlink "${clickedItem?.name}" from this dashboard?`}
        actionButtonText="Unlink"
        style={{ 
          zIndex: 99999,  // Increased z-index
          position: 'relative'  // Ensure it's above the backdrop
        }}
        overlayStyle={{  // Add overlay style
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99998,  // Just below the modal
          display: showUnlinkConfirmation ? 'block' : 'none'
        }}
      />
      <ConfirmationModal
        isOpen={showMoveConfirmation}
        onClose={() => {
          setShowMoveConfirmation(false);
          setDestinationNode(null);
        }}
        onConfirm={async () => {
          try {
            if (nodeToMove && destinationNode) {
              const success = await onMoveNode(nodeToMove.data, destinationNode.data);
              if (success) {
                moveNodeInTree(localTreeData, nodeToMove.data.id, destinationNode.data.id);
                setLocalTreeData({ ...localTreeData });
              }
            }
          } catch (error) {
            console.error('Error moving node:', error);
          } finally {
            setShowMoveConfirmation(false);
            setDestinationNode(null);
            setNodeToMove(null);
            setIsMoveMode(false);
          }
        }}
        title="Move Node"
        message={
          nodeToMove && destinationNode
            ? `Move "${nodeToMove.data.name}" under "${destinationNode.data.name}"?`
            : 'No source/destination selected.'
        }
        actionButtonText="Move"
        style={{ zIndex: 9999 }}
      />
      {isEmployeeMode && skillLevelPosition && clickedItem && createPortal(
        <div 
          className="skill-level-selector"
          style={{ 
            position: 'fixed',
            left: `${skillLevelPosition.x}px`,
            top: `${skillLevelPosition.y}px`,
            transform: 'translateY(-50%)',
            zIndex: 9999,
            backgroundColor: 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <SkillLevelSelector
            selectedLevelId={selectedSkillLevel}
            onLevelSelect={handleSkillLevelSelect}
            skillName={clickedItem.name}
            skillLevels={skillLevels}
          />
        </div>,
        document.body
      )}
    </TreeContainer>
  );
};

export default TreeChart;
